import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Sidebar from '../../layout/Sidebar/SideBar';
import Navbar from '../../layout/Navbar/Navbar';
import blogFetch from '../../axios/config';
import toast from 'react-hot-toast';
import { PecaOpaco, TipoProblema } from './types';

const TIPOS_PROBLEMA: { valor: TipoProblema; classe: string }[] = [
    { valor: 'DEFEITO', classe: 'danger' },
    { valor: 'RETALHO', classe: 'warning' },
    { valor: 'AMOSTRA', classe: 'info' },
];

export function ReporteOpaco() {
    // ── Operador (lido do localStorage) ──
    const [matriculaSAP, setMatriculaSAP] = useState('');
    const [nomeUsuario, setNomeUsuario] = useState('');

    // ── Ordem ──
    const [ordemInput, setOrdemInput] = useState('');
    const [peca, setPeca] = useState<PecaOpaco | null>(null);
    const ordemRef = useRef<HTMLInputElement>(null);

    // ── Problema ──
    const [tipoProblema, setTipoProblema] = useState<TipoProblema | null>(null);
    const [largura, setLargura] = useState('');
    const [altura, setAltura] = useState('');
    const [observacao, setObservacao] = useState('');

    // ── Submissão ──
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ══════════════════════════════════════
    //  Matrícula SAP (lê do localStorage)
    // ══════════════════════════════════════

    useEffect(() => {
        const raw = localStorage.getItem('listaMontagem_matriculas_SAP');
        if (!raw) {
            toast.error('Nenhuma matrícula SAP salva. Registre-se primeiro na tela de Montagem.');
            return;
        }
        try {
            const obj = JSON.parse(raw);
            setMatriculaSAP(obj.matriculaSAP || '');
            setNomeUsuario(obj.nome || '');
        } catch {
            toast.error('Erro ao ler matrícula salva.');
        }
    }, []);

    // ══════════════════════════════════════
    //  Busca dados da ordem
    // ══════════════════════════════════════

    useEffect(() => {
        const valor = ordemInput.trim();
        if (!valor) {
            setPeca(null);
            return;
        }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await blogFetch.get(`ISAGA_ReporteOpaco/getPeca/${encodeURIComponent(valor)}`);
                if (!data) {
                    toast.error('Ordem não encontrada.');
                    setPeca(null);
                    return;
                }
                setPeca({
                    ordem: valor,
                    tipoPeca: data.ordem_Part_Short || '',
                    codigoVeiculo: data.ordem_VehCod || '',
                    projeto: data.subProduto_Desc || '',
                    veiculo: data.ordem_VehDesc || '',
                    ultimoApont: data.ordem_UltSetApontado || '',
                    setorAtual: data.ordem_setAtual || '',
                });
            } catch (error: any) {
                console.error('Erro ao buscar ordem:', error);
                toast.error(error.response?.data || 'Erro ao buscar ordem.');
                setPeca(null);
            }
        }, 500);
        return () => clearTimeout(timeout);
    }, [ordemInput]);

    // ══════════════════════════════════════
    //  Submit
    // ══════════════════════════════════════

    const OBS_MAX = 500;
    const precisaDimensoes = tipoProblema === 'DEFEITO' || tipoProblema === 'RETALHO';
    const larguraNum = parseFloat(largura);
    const alturaNum = parseFloat(altura);
    const dimensoesValidas = !precisaDimensoes || (larguraNum > 0 && alturaNum > 0);
    const podeSubmeter = !!matriculaSAP && !!peca && !!tipoProblema && dimensoesValidas;

    const handleSubmit = async () => {
        if (!podeSubmeter || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const payload: any = {
                Ordem: peca!.ordem,
                Matricula: matriculaSAP,
                TipoProblema: tipoProblema,
                Observacao: observacao.trim() || undefined,
            };
            if (precisaDimensoes) {
                payload.Largura = larguraNum;
                payload.Altura = alturaNum;
            }
            await blogFetch.post('ISAGA_ReporteOpaco/registrar', payload);
            toast.success('Problema registrado com sucesso!');
            setOrdemInput('');
            setPeca(null);
            setTipoProblema(null);
            setLargura('');
            setAltura('');
            setObservacao('');
            ordemRef.current?.focus();
        } catch (error: any) {
            console.error('Erro ao registrar problema:', error);
            const apiData = error.response?.data;
            const msg = apiData?.message || (typeof apiData === 'string' ? apiData : null);
            toast.error(msg || 'Erro ao registrar problema.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTipoClick = (valor: TipoProblema) => {
        const novo = tipoProblema === valor ? null : valor;
        setTipoProblema(novo);
        if (novo === 'AMOSTRA' || novo === null) {
            setLargura('');
            setAltura('');
        }
    };

    // ══════════════════════════════════════
    //  Render
    // ══════════════════════════════════════

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body d-flex flex-column align-items-center px-2 px-sm-3">
                <div style={{ width: '100%', maxWidth: 900 }}>
                    <h4 className="fw-bold text-primary mb-3 text-center text-md-start">
                        Reporte de Problemas — Opaco
                    </h4>

                    {nomeUsuario && (
                        <small className="text-success fw-semibold d-block mb-2">
                            Operador: {nomeUsuario}
                        </small>
                    )}

                    {/* Ordem */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-body">
                            <Form onSubmit={(e) => e.preventDefault()}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Ordem</Form.Label>
                                    <Form.Control
                                        type="text"
                                        ref={ordemRef}
                                        size="lg"
                                        value={ordemInput}
                                        onChange={(e) => setOrdemInput(e.target.value)}
                                        placeholder="Bipe ou digite a ordem"
                                        autoFocus
                                    />
                                </Form.Group>
                            </Form>
                        </div>
                    </div>

                    {/* Info da Ordem + Tipo + Dimensões + Obs (só aparece depois de carregar a peça) */}
                    {peca && (
                        <>
                            <div className="card shadow-sm mb-3">
                                <div className="card-body">
                                    <h6 className="fw-bold text-dark mb-3">Informações da Ordem</h6>
                                    <div className="row g-2 g-md-3">
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Tipo Peça</label>
                                            <input value={peca.tipoPeca} className="form-control" readOnly />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Código Veículo</label>
                                            <input value={peca.codigoVeiculo} className="form-control" readOnly />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Setor Atual</label>
                                            <input value={peca.setorAtual} className="form-control" readOnly />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Último Apontamento</label>
                                            <input value={peca.ultimoApont} className="form-control" readOnly />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Projeto</label>
                                            <input value={peca.projeto} className="form-control" readOnly />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label className="form-label small fw-bold text-dark mb-1">Veículo</label>
                                            <input value={peca.veiculo} className="form-control" readOnly />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card shadow-sm mb-3">
                                <div className="card-body">
                                    <h6 className="fw-bold text-dark mb-3">Tipo de Problema</h6>
                                    <div className="d-grid gap-2 mb-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                        {TIPOS_PROBLEMA.map(({ valor, classe }) => (
                                            <button
                                                key={valor}
                                                type="button"
                                                className={`btn btn-lg py-3 fw-bold ${
                                                    tipoProblema === valor
                                                        ? `btn-${classe} text-dark`
                                                        : `btn-outline-${classe} text-dark`
                                                }`}
                                                style={{ fontSize: '1.1rem' }}
                                                onClick={() => handleTipoClick(valor)}
                                            >
                                                {valor}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Largura e Altura — só para DEFEITO e RETALHO */}
                                    {precisaDimensoes && (
                                        <div className="row g-2 mb-3">
                                            <div className="col-6">
                                                <Form.Label className="fw-bold text-dark">Largura (cm)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="lg"
                                                    min={0.1}
                                                    step="any"
                                                    value={largura}
                                                    onChange={(e) => setLargura(e.target.value)}
                                                    placeholder="Largura"
                                                    isInvalid={largura !== '' && !(parseFloat(largura) > 0)}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <Form.Label className="fw-bold text-dark">Altura (cm)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="lg"
                                                    min={0.1}
                                                    step="any"
                                                    value={altura}
                                                    onChange={(e) => setAltura(e.target.value)}
                                                    placeholder="Altura"
                                                    isInvalid={altura !== '' && !(parseFloat(altura) > 0)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold text-dark">Observação</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            maxLength={OBS_MAX}
                                            value={observacao}
                                            onChange={(e) => setObservacao(e.target.value)}
                                            placeholder="Observação (opcional)"
                                            disabled={!tipoProblema}
                                            style={{ fontSize: '1rem' }}
                                        />
                                        {observacao.length > 0 && (
                                            <div className="text-end mt-1">
                                                <small className={observacao.length >= OBS_MAX ? 'text-danger' : 'text-muted'}>
                                                    {observacao.length}/{OBS_MAX}
                                                </small>
                                            </div>
                                        )}
                                    </Form.Group>

                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg w-100 py-3"
                                        style={{ fontSize: '1.1rem' }}
                                        onClick={handleSubmit}
                                        disabled={!podeSubmeter || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Enviando...
                                            </>
                                        ) : (
                                            'Enviar Reporte'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                </div>
            </div>
        </div>
    );
}
